import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { DatePicker, Input, Modal, Spin, message } from "antd";
import dayjs from "dayjs";
import {
  fetchReviewNotesApi,
  reviewActionApi,
  reviewQueueActionApi,
} from "../../../service/matter";
import { decideBoardAppealReview } from "../../../service/inbox";
import {
  InboxItemFooterRow,
  InboxItemMeta,
  InboxItemRow,
} from "../../../components/AIPane/AIPaneStyled";
import {
  WorkItemInstruction,
  WorkItemSubjectButton,
  WorkPanelHint,
  WorkStateLine,
  WorkThreadAuthor,
  WorkThreadBody,
  WorkThreadEntry,
  WorkThreadList,
  WorkVerbBar,
  WorkVerbButton,
} from "../../../components/WorkItems/WorkItemsStyled";

/**
 * A document sent for review, as a card with verbs — the twin of
 * WorkItemCard.
 *
 * Matthew, 9/10, looking at Anne's Home beside his own: "when she
 * clicks the work item it just brings her to the doc. Why the
 * difference? I need everyone to have the same version of work docs I
 * do." There was no version difference. Two unlike things share the
 * page: work items (the new instruction loop, which expand to a card
 * with every verb) and document review shares (the older mechanism,
 * which only ever opened the document). Anne holds both, so she saw
 * the seam inside her own list; Paige holds nothing but shares, so
 * every row she clicked opened a document and none of them could ever
 * be finished.
 *
 * The finishing move was the sharper half of it. When a reviewer
 * approves, the document goes back to whoever sent it, to do the real
 * work — file the brief with the court. Closing that out lived on the
 * old Tasks tab; when My Work replaced that tab the verb did not come
 * across, so 62 exchanges firm-wide had no way to end. This card is
 * where they end.
 *
 * The card reads the exchange from the caller's side:
 *
 *   * **it came back to me** (I sent it; the reviewer has ruled) —
 *     Filed / Done with the date it actually happened, or Dismiss;
 *   * **it is mine to review** — Approve, or send it back for rework.
 *
 * "Filed" is the CAVC wording, and only on CAVC rows: a brief is filed
 * with the court, a rating decision review is merely done. Same verb
 * underneath either way (Matthew: "still appropriate to the task").
 */

const APPROVED = "APPROVED";
const REWORK = "REWORK";

/**
 * Whether this row is an exchange the reader can act on, and from
 * which side. Read-only plates (someone else's work) get nothing.
 *
 * The server says outright whose move it is, because the same row
 * shape also describes a share someone *else* is holding — identical
 * but for those flags. Older backends omit them, so fall back to
 * reading the state; that inference is right for everything in the
 * reader's own court, which is all this page renders.
 */
export const reviewStanceOf = (row, readOnly) => {
  if (readOnly || row?.kind !== "doc_review") return null;
  const review = row.review || {};
  if (!review.share_id) return null;
  const settled = review.status !== APPROVED && review.status !== REWORK;

  if (review.can_finish != null || review.can_review != null) {
    if (review.can_finish && !settled) return "returned";
    if (review.can_review) return "to_review";
    return null;
  }

  if (review.is_returned_outcome) return settled ? null : "returned";
  return "to_review";
};

/** CAVC filings get filed; everything else just gets done. */
const finishVerbFor = (row) =>
  row?.category === "cavc"
    ? { verb: "Filed", title: "Mark as filed", dateLabel: "Filed on" }
    : { verb: "Done", title: "Close this out", dateLabel: "Finished on" };

/**
 * Looking on, not acting: the server says neither move is this reader's
 * and nothing has come back to them. That is the sender watching their
 * own request, a colleague reading the matter page, or the reviewer
 * seeing the copy of a review they have already judged — the card then
 * reports where the review stands rather than addressing the reader.
 * Older backends omit the flags, and every row Home renders is the
 * reader's own, so absent flags never read as looking on.
 */
const isLookingOn = (review) =>
  review.can_review === false &&
  review.can_finish === false &&
  !review.is_returned_outcome;

/**
 * The state of a review the reader is only looking on at. The actor of
 * an approval or a rework is the reviewer — named by the server when
 * the row came from a work-item copy (`reviewer_name`), and "You" when
 * the reader is that reviewer; `counterparty` is the *other* party and
 * would name the sender as having approved their own document.
 */
const lookingOnHeadline = (review, who) => {
  const reviewer = review.reviewer_name || who;
  const me = review.reader_is_reviewer === true;
  if (review.status === APPROVED) {
    return me ? `You approved this — ${who} closes it out` : `${reviewer} approved this`;
  }
  if (review.status === REWORK) {
    return me
      ? `You sent this back for rework — with ${who}`
      : `${reviewer} sent this back for rework`;
  }
  if (review.status === "CANCELLED") return "This review was cancelled";
  return review.board_appeal
    ? `${reviewer} is deciding whether to appeal this Board decision`
    : `${reviewer} is reviewing this`;
};

/** The one-line summary above the fold: what state this exchange is in
 * and whose move it is. */
const headlineFor = (row) => {
  const who = row.counterparty || "someone";
  const review = row.review || {};
  if (isLookingOn(review)) return lookingOnHeadline(review, who);
  if (review.board_appeal && !review.is_returned_outcome) {
    return `${who} asked whether to appeal this Board decision`;
  }
  if (!review.is_returned_outcome) return `${who} asked you to review this`;
  return review.status === APPROVED
    ? `${who} approved this — over to you`
    : `${who} sent this back for rework`;
};

/**
 * Filed/Done asks for the day it happened, because the filing and the
 * clicking are rarely the same day: a brief filed Monday gets closed
 * out on Wednesday, and the record should say Monday.
 */
const FinishModal = ({ open, row, busy, onClose, onConfirm }) => {
  const { title, dateLabel } = finishVerbFor(row);
  const [day, setDay] = useState(() => dayjs());
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setDay(dayjs());
      setNote("");
    }
  }, [open]);

  return (
    <Modal
      open={open}
      title={title}
      okText={title}
      onCancel={busy ? undefined : onClose}
      onOk={() => onConfirm(day, note)}
      okButtonProps={{ loading: busy, disabled: !day }}
      cancelButtonProps={{ disabled: busy }}
      destroyOnClose
    >
      <label htmlFor="review-finish-date">{dateLabel}</label>
      <DatePicker
        id="review-finish-date"
        value={day}
        onChange={setDay}
        allowClear={false}
        format="ddd D MMM YYYY"
        // Nothing was filed tomorrow; the server rejects it anyway.
        disabledDate={(candidate) => candidate.isAfter(dayjs(), "day")}
        style={{ display: "block", marginTop: 4, marginBottom: 12 }}
      />
      <label htmlFor="review-finish-note">Note (optional)</label>
      <Input.TextArea
        id="review-finish-note"
        autoSize={{ minRows: 2, maxRows: 6 }}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        maxLength={4000}
        placeholder="Anything worth remembering about this filing…"
        disabled={busy}
        style={{ marginTop: 4 }}
      />
    </Modal>
  );
};

FinishModal.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object.isRequired,
  busy: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

const ReviewCard = ({ row, stance, matterLabel, onActed, onOpenDocument }) => {
  const review = row.review || {};
  const [notes, setNotes] = useState(null);
  const [note, setNote] = useState("");
  const [busyVerb, setBusyVerb] = useState(null);
  const [finishOpen, setFinishOpen] = useState(false);
  const [boardDate, setBoardDate] = useState(null);
  const appeal = review.board_appeal;
  const { verb } = finishVerbFor(row);

  useEffect(() => {
    const documentId = row.document?.id;
    if (!documentId) {
      setNotes([]);
      return;
    }
    fetchReviewNotesApi(documentId)
      .then((response) => setNotes(response?.data?.review_notes || []))
      .catch(() => setNotes([]));
  }, [row.document?.id]);

  const run = useCallback(
    (verbKey, call, said) => {
      setBusyVerb(verbKey);
      call()
        .then(() => {
          message.success(said);
          setNote("");
          setFinishOpen(false);
          onActed();
        })
        .catch((err) => {
          message.error(
            err?.response?.data?.error ||
              err?.response?.data?.detail ||
              "Couldn't do that. Please try again."
          );
        })
        .finally(() => setBusyVerb(null));
    },
    [onActed]
  );

  const finish = useCallback(
    (day, finishNote) =>
      run(
        "finish",
        () =>
          reviewQueueActionApi(review.share_id, "complete", finishNote, {
            resolvedOn: day.format("YYYY-MM-DD"),
          }),
        `${row.title} marked ${verb.toLowerCase()} ${day.format("D MMM")}.`
      ),
    [run, review.share_id, row.title, verb]
  );

  const appealDecide = useCallback(
    (action) => {
      const trimmed = note.trim();
      if (!trimmed) {
        message.warning("Write the note that goes back to them.");
        return;
      }
      run(
        action,
        () =>
          decideBoardAppealReview(
            appeal.id,
            action,
            trimmed,
            boardDate ? boardDate.format("YYYY-MM-DD") : null
          ),
        action === "signUp"
          ? `Signed up — ${row.counterparty || "they"} will get your note.`
          : `Turned down — ${row.counterparty || "they"} will get your note.`
      );
    },
    [run, note, appeal, boardDate, row.counterparty]
  );

  const decide = useCallback(
    (status) => {
      const trimmed = note.trim();
      if (status === REWORK && !trimmed) {
        message.warning("Say what needs changing — they can't guess.");
        return;
      }
      run(
        status,
        () => reviewActionApi(review.share_id, status, trimmed),
        status === APPROVED
          ? `Approved — back to ${row.counterparty || "the sender"}.`
          : `Sent back to ${row.counterparty || "the sender"}.`
      );
    },
    [run, note, review.share_id, row.counterparty]
  );

  const busy = busyVerb != null;

  return (
    <InboxItemRow data-testid={`review-card-${review.share_id}`}>
      <WorkItemSubjectButton type="button" onClick={onOpenDocument}>
        {row.title || "Untitled"}
      </WorkItemSubjectButton>
      <InboxItemFooterRow>
        <InboxItemMeta>
          {matterLabel ? `${matterLabel} · ` : ""}
          {headlineFor(row)}
        </InboxItemMeta>
      </InboxItemFooterRow>
      {row.step_line && <WorkStateLine>{row.step_line}</WorkStateLine>}

      {review.notes && (
        <WorkItemInstruction>{review.notes}</WorkItemInstruction>
      )}

      <WorkPanelHint style={{ marginTop: 8 }}>
        <WorkItemSubjectButton type="button" onClick={onOpenDocument}>
          Open document
        </WorkItemSubjectButton>
      </WorkPanelHint>

      {notes === null ? (
        <Spin size="small" style={{ marginTop: 8 }} />
      ) : (
        notes.length > 0 && (
          <WorkThreadList>
            {notes.map((entry) => (
              <WorkThreadEntry key={entry.id}>
                <WorkThreadAuthor>
                  {entry.reviewer_name}
                  {entry.action_type ? ` · ${entry.action_type}` : ""}
                  {entry.created_at
                    ? ` · ${dayjs(entry.created_at).format("D MMM")}`
                    : ""}
                </WorkThreadAuthor>
                <WorkThreadBody>{entry.comment_text}</WorkThreadBody>
              </WorkThreadEntry>
            ))}
          </WorkThreadList>
        )
      )}

      {stance === "to_review" && (
        <Input.TextArea
          autoSize={{ minRows: 2, maxRows: 6 }}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="What they should know…"
          maxLength={4000}
          style={{ marginTop: 8 }}
          aria-label={`Note on ${row.title || "this document"}`}
        />
      )}

      <WorkVerbBar>
        {stance === "returned" && (
          <>
            <WorkVerbButton
              type="button"
              $primary
              disabled={busy}
              onClick={() => setFinishOpen(true)}
              data-testid={`review-finish-${review.share_id}`}
            >
              {busyVerb === "finish" ? "Saving…" : `${verb}\u2026`}
            </WorkVerbButton>
            <WorkVerbButton
              type="button"
              disabled={busy}
              onClick={() =>
                run(
                  "dismiss",
                  () => reviewQueueActionApi(review.share_id, "dismiss"),
                  "Cleared off your list."
                )
              }
            >
              {busyVerb === "dismiss" ? "Dismissing…" : "Dismiss"}
            </WorkVerbButton>
            {review.status === REWORK && (
              <WorkVerbButton
                type="button"
                disabled={busy}
                onClick={() =>
                  run(
                    "send_back",
                    () =>
                      reviewQueueActionApi(
                        review.share_id,
                        "send_back",
                        note.trim()
                      ),
                    `Back to ${row.counterparty || "the reviewer"} for another look.`
                  )
                }
              >
                {busyVerb === "send_back"
                  ? "Sending…"
                  : "Send back for another look"}
              </WorkVerbButton>
            )}
          </>
        )}
        {stance === "to_review" && appeal && (
          <>
            <div style={{ flexBasis: "100%" }}>
              <label htmlFor={`board-date-${review.share_id}`}>
                Board date, if you have it
              </label>
              <DatePicker
                id={`board-date-${review.share_id}`}
                value={boardDate}
                onChange={setBoardDate}
                format="MMM D, YYYY"
                style={{ display: "block", marginTop: 4 }}
                disabled={busy}
              />
            </div>
            <WorkVerbButton
              type="button"
              $primary
              disabled={busy}
              onClick={() => appealDecide("signUp")}
              data-testid={`review-sign-up-${review.share_id}`}
            >
              {busyVerb === "signUp" ? "Signing up…" : "Sign up"}
            </WorkVerbButton>
            <WorkVerbButton
              type="button"
              disabled={busy}
              onClick={() => appealDecide("turnDown")}
              data-testid={`review-turn-down-${review.share_id}`}
            >
              {busyVerb === "turnDown" ? "Turning down…" : "Turn down"}
            </WorkVerbButton>
          </>
        )}
        {stance === "to_review" && !appeal && (
          <>
            <WorkVerbButton
              type="button"
              $primary
              disabled={busy}
              onClick={() => decide(APPROVED)}
              data-testid={`review-approve-${review.share_id}`}
            >
              {busyVerb === APPROVED ? "Approving…" : "Approve"}
            </WorkVerbButton>
            <WorkVerbButton
              type="button"
              disabled={busy}
              onClick={() => decide(REWORK)}
            >
              {busyVerb === REWORK ? "Sending…" : "Send back for rework"}
            </WorkVerbButton>
          </>
        )}
      </WorkVerbBar>

      <FinishModal
        open={finishOpen}
        row={row}
        busy={busyVerb === "finish"}
        onClose={() => setFinishOpen(false)}
        onConfirm={finish}
      />
    </InboxItemRow>
  );
};

ReviewCard.propTypes = {
  row: PropTypes.object.isRequired,
  /** "returned" (it came back to me) or "to_review" (mine to judge) —
   * from {@link reviewStanceOf}. `null` when nothing here is the
   * reader's move (a work-item copy seen by the sender or a colleague):
   * the card reports the state and offers no verbs. */
  stance: PropTypes.oneOf(["returned", "to_review"]),
  matterLabel: PropTypes.string,
  onActed: PropTypes.func.isRequired,
  onOpenDocument: PropTypes.func.isRequired,
};

export default ReviewCard;
